
import React, { useRef, useCallback, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { isSocialMediaBrowser } from '@/utils/browser-detection';

interface BulletinInteractionWrapperProps {
  onImageClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

const BulletinInteractionWrapper = ({ 
  onImageClick, 
  children 
}: BulletinInteractionWrapperProps) => {
  const isMobile = useIsMobile();
  const doubleClickRef = useRef(false);
  const lastClickTimeRef = useRef(0);
  const touchStartRef = useRef<{ x: number, y: number } | null>(null);
  const hasMoveRef = useRef(false);
  const isInSocialBrowserRef = useRef(false);

  // Detect if in social media browser on mount
  useEffect(() => {
    isInSocialBrowserRef.current = isSocialMediaBrowser();
  }, []);

  // Use useCallback to optimize the handler function
  const handleClick = useCallback((e: React.MouseEvent) => {
    // Get debugging info from the clicked element
    const target = e.target as HTMLElement;
    const bulletinId = target.closest('[data-bulletin-id]')?.getAttribute('data-bulletin-id');
    const isLiked = target.closest('[data-liked]')?.getAttribute('data-liked');
    
    console.log('DEBUG - BulletinInteractionWrapper - Click detected:', {
      bulletinId,
      isLiked,
      targetElement: target.tagName,
      targetClasses: target.className,
      e: e.type,
      isSocialBrowser: isInSocialBrowserRef.current
    });

    const currentTime = Date.now();
    const timeSinceLastClick = currentTime - lastClickTimeRef.current;
    lastClickTimeRef.current = currentTime;
    
    // In social media browsers, prioritize single click handling
    if (isInSocialBrowserRef.current) {
      // Don't handle if the click is on an interactive UI element (buttons)
      const isInteractiveButton = target.closest('button:not([data-bulletin-id])'); 
      const isInteractiveRole = target.closest('[role="button"]:not([data-bulletin-id])');
      const isDialog = target.closest('[role="dialog"]');
      
      const isInteractiveElement = isInteractiveButton || isInteractiveRole || isDialog;
      
      if (!isInteractiveElement) {
        console.log('DEBUG - SOCIAL BROWSER - Opening inspection view');
        onImageClick(e);
        return;
      }
    }
    
    // Regular behavior for standard browsers
    // Check if this might be a double-click (within 300ms)
    if (timeSinceLastClick < 300) {
      // Mark as double-click and return early to allow double-click handlers to work
      doubleClickRef.current = true;
      setTimeout(() => {
        doubleClickRef.current = false;
      }, 500); // Increased from 300ms to 500ms
      return;
    }
    
    // Don't handle if the click is on an interactive UI element (buttons)
    const isInteractiveButton = target.closest('button:not([data-bulletin-id])'); 
    const isInteractiveRole = target.closest('[role="button"]:not([data-bulletin-id])');
    const isDialog = target.closest('[role="dialog"]');
    
    const isInteractiveElement = isInteractiveButton || isInteractiveRole || isDialog;
    
    // For single clicks, only handle if it's not a recent double click and not an interactive element
    if (!doubleClickRef.current && !isInteractiveElement) {
      console.log('DEBUG - OPENING INSPECTION VIEW - BulletinInteractionWrapper');
      onImageClick(e);
    }
  }, [onImageClick]);

  // Handle touch start to detect potential scroll actions
  const handleTouchStart = (e: React.TouchEvent) => {
    // Store initial position for movement tracking
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY
    };
    hasMoveRef.current = false;
  };
  
  // Handle touch move to detect scrolling
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    
    const touch = e.touches[0];
    const moveX = Math.abs(touch.clientX - touchStartRef.current.x);
    const moveY = Math.abs(touch.clientY - touchStartRef.current.y);
    
    // Mark as moved if distance exceeds threshold
    if (moveX > 10 || moveY > 10) {
      hasMoveRef.current = true;
    }
  };

  return (
    <div 
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={(e) => {
        // If in social media browser and no movement, treat as tap
        if (isInSocialBrowserRef.current && !hasMoveRef.current) {
          const fakeEvent = { target: e.target } as unknown as React.MouseEvent;
          handleClick(fakeEvent);
        }
        
        // Prevent opening if there was movement (scrolling)
        if (hasMoveRef.current) {
          e.stopPropagation();
          touchStartRef.current = null;
          hasMoveRef.current = false;
          return;
        }
        
        touchStartRef.current = null;
      }}
      className="relative w-full h-full overflow-hidden bg-black/30 rounded-lg flex items-center justify-center"
      style={{ 
        height: "100%",
        touchAction: "pan-y" // Allow vertical scroll while suppressing lateral drag wobble
      }}
    >
      {children}
    </div>
  );
};

export default BulletinInteractionWrapper;
